/* eslint-disable react/jsx-no-constructed-context-values */
import { useQuery } from "@tanstack/react-query";
import { getBrandMagnet } from "apis/brand";
import React, { useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { getActivationsForMagnet } from "apis/magnets";
import { BrandContext } from "../../components/context/BrandContext";
import { useRouter } from "../../components/hooks";
import { BRAND_MAGNET, PUBLISH, TOAST_CONFIG } from "../../constants";
import { get, post } from "../../utils/request";

export type DataProviderContextType = {
  magnetDetailsState: BrandMagnetDetails | null;
  magnetMementosFetchingError: unknown;
  isMagnetMementosLoading: boolean;
  triggerRefetchMagnetMementos: () => void;
  isRefreshingMagnetMementos: boolean;
  magnetMementos: Memento[];
  error: any;
  loading: boolean;
  brandId: string | null | undefined;
  magnetId: string;
  reloadMagnetDetails: () => any;
  publishMagnet: (
    magnetId: string,
    handleSuccess: (data: any) => any,
    handleError: (errorMessage: string, errorData: any) => any,
  ) => any;
  isRefetching: boolean;
};

export const DataProviderContext = React.createContext<DataProviderContextType>({
  magnetDetailsState: null,
  magnetMementos: [],
  magnetMementosFetchingError: null,
  isMagnetMementosLoading: false,
  triggerRefetchMagnetMementos: () => {},
  isRefreshingMagnetMementos: false,
  loading: false,
  error: null,
  brandId: "",
  magnetId: "",
  reloadMagnetDetails: () => {},
  publishMagnet: () => {},
  isRefetching: false,
});

export default function DataProvider({ children }: any) {
  const { brandId } = useContext(BrandContext);
  const router = useRouter();
  const { m } = router.query;
  const magnetId = m || (router.pathname.includes("magnet/") && router.pathname.split("magnet/")[1].split("/")[0]);

  const handleGetMagnetFailure = () => {
    throw new Error("Failed to get Magnet details.");
  };

  const getMagnetDetailsForBrand = () =>
    get(getBrandMagnet(brandId as string, magnetId), {
      processError: handleGetMagnetFailure,
    });

  const {
    isLoading: loading,
    data,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: [brandId, magnetId],
    queryFn: getMagnetDetailsForBrand,
    enabled: !!brandId && !!magnetId,
  });

  const {
    error: magnetMementosFetchingError,
    data: magnetMementos,
    isLoading: isMagnetMementosLoading,
    refetch: triggerRefetchMagnetMementos,
    isRefetching: isRefreshingMagnetMementos,
  } = useQuery({
    queryKey: ["magnet_mementos", magnetId],
    queryFn: () =>
      magnetId
        ? get(getActivationsForMagnet(magnetId), {
            processError: (errorMessage) => {
              throw Error(errorMessage);
            },
          })
        : null,
  });

  const reloadMagnetDetails = () => refetch();

  const publishMagnet = (
    id: string,
    handleSuccess: (data: any) => any,
    handleError: (errorMessage: string, errorData: any) => any,
  ) =>
    post(
      BRAND_MAGNET + id + PUBLISH,
      {},
      {
        processData: handleSuccess,
        processError: handleError,
      },
    );

  useEffect(() => {
    if (error) toast.error("Failed to fetch Magnet details", TOAST_CONFIG);
  }, [error]);

  const context = {
    magnetDetailsState: data,
    magnetMementos,
    magnetMementosFetchingError,
    isMagnetMementosLoading,
    triggerRefetchMagnetMementos,
    isRefreshingMagnetMementos,
    error,
    loading,
    brandId,
    magnetId,
    reloadMagnetDetails,
    publishMagnet,
    isRefetching,
  };
  return <DataProviderContext.Provider value={context}>{children}</DataProviderContext.Provider>;
}

export const useDataProviderContext = () => {
  const context = useContext(DataProviderContext);
  return context;
};
